/**
 * Coming back from the payment gateway.
 *
 * Checkout hands the browser to the provider's hosted page, and the provider
 * sends it back to `/shop?topup=return&order=<id>` (core appends the order id;
 * paid, declined or abandoned all land there — the order's status says which).
 * That is not a route the app has, so `App.vue` reads it once on load, opens the
 * Chandler with the order id, and clears the URL.
 *
 * The shop then asks core (`GET /api/payments/deposits/:id`) until the webhook
 * has settled the order. If the id is missing from the URL, the one core kept
 * in `sessionStorage` when the deposit started is used.
 */
import { clearPendingOrder, readReturnedOrderId } from '../core/payments';

/** `orderId` is null when neither the URL nor the session remembered one. */
export interface TopupReturn {
  orderId: string | null;
}

/** What core's order ids look like (`ADMP-…`): anything else is not ours to poll. */
const ORDER_ID = /^[A-Za-z0-9_-]{1,64}$/;

/** Pure: is this URL the gateway's return, and which order does it name (`remembered` is the fallback)? */
export function parseTopupReturn(pathname: string, search: string, remembered: string | null): TopupReturn | null {
  const path = pathname.replace(/\/+$/, '').toLowerCase();
  if (path !== '/shop') return null;
  const params = new URLSearchParams(search);
  if (params.get('topup') !== 'return') return null;
  const id = params.get('order') ?? remembered;
  return { orderId: id && ORDER_ID.test(id) ? id : null };
}

/** Read and consume the return, so a reload does not announce it twice. */
export function readTopupReturn(): TopupReturn | null {
  if (typeof window === 'undefined') return null;
  const { pathname, search } = window.location;
  const r = parseTopupReturn(pathname, search, readReturnedOrderId(search));
  if (!r) return null;
  clearPendingOrder();
  if (window.history?.replaceState) window.history.replaceState(null, '', '/');
  return r;
}
